import "./ProjectAccessInviteFields.css";

import { FormEvent } from "react";

import { SkeletonOrgDetails } from "../../../models";
import { ErrorBox, InformationBox } from "../../cards";
import { ActionButtons, Autocomplete, Checkbox, FormLabel, Select } from "../../forms";
import { Card, Column, Row } from "../../layout";
import { NotOnKanaDeveloperInviteFields } from "../NotOnKanaDeveloperInviteFields";
import { getErrorMessages, hasFieldError } from "../utils";
import { ProjectAccessInviteFieldsProps } from "./models";

const emptySkeletonOrgDetails: SkeletonOrgDetails = {
  email: "",
  organisationDisplayName: "",
  invitedUserFirstName: "",
  invitedUserLastName: "",
};

export const ProjectAccessInviteFields = ({
  projectOptions,
  projectUuid,
  onProjectChange,
  onProjectSearch,
  organisationTypeOptions,
  organisationOptions,
  onOrganisationSearch,
  accessOptions,
  value,
  onChange,
  onSubmit,
  onCancel,
  showMakeResponsibleOwner,
  showExistingDeveloperAction = false,
  existingDeveloperActionOptions = [],
  existingDeveloperName,
  createNewOrganisation,
  skeletonOrgDetails = emptySkeletonOrgDetails,
  onSkeletonOrgDetailsChange,
  isLoading = false,
  corners = "default",
  errors,
}: ProjectAccessInviteFieldsProps): JSX.Element => {
  const isTypeChosen = !!value.organisationType;
  const hasProjectPicker = !!projectOptions;

  const makeResponsibleOwnerField = (
    <div className="ProjectAccessInviteFields_checkbox">
      <FormLabel label="Make responsible owner" />
      <Checkbox
        label="Confirm"
        checked={value.makeResponsibleOwner ?? false}
        disabled={!isTypeChosen}
        onChange={(e) => onChange({ ...value, makeResponsibleOwner: e.target.checked })}
      />
    </div>
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Card corners={corners}>
      <form className="ProjectAccessInviteFields" onSubmit={handleSubmit}>
        <Row spacingV="ml">
          <Column span={12}>
            <h2>New access invitation</h2>
          </Column>
        </Row>

        <Row spacingV="ll">
          <Column span={12}>
            <InformationBox variant="info">
              {hasProjectPicker ? (
                <p className="body2">
                  Only projects your organisation is the responsible owner of are shown in the project dropdown.
                  Granting access will allow the chosen organisation the ability to read, or read and edit, your
                  selected project. Access can be granted on a guest basis or permanently. Once granted, access can be
                  later revoked. The invitation request will expire after 10 days.
                </p>
              ) : (
                <p className="body2">
                  Granting access to your project will allow the chosen organisation the ability to read, or read and
                  edit, your selected project. Access can be granted on a guest basis or permanently. Once granted,
                  access can be later revoked. Sending an invitation will issue an notification to your selected
                  developer organisation. The invitation request will expire after 10 days.
                </p>
              )}
            </InformationBox>
          </Column>
        </Row>

        {errors && errors.length > 0 && (
          <Row spacingV="ml">
            <Column span={12}>
              <ErrorBox
                title="Please solve the below issues before sending the invitation"
                text={getErrorMessages(errors)}
              />
            </Column>
          </Row>
        )}
        <Row spacingV="ml">
          {hasProjectPicker && (
            <Column span={6}>
              <Autocomplete
                label="Project"
                placeholder="Start typing to find the project"
                corners={corners}
                data={projectOptions}
                initialKey={projectUuid}
                onSelect={(uuid) => onProjectChange?.(uuid)}
                onTypeSearch={onProjectSearch}
              />
            </Column>
          )}
          <Column span={6}>
            <Select
              label="Organisation type"
              placeholder="Select"
              corners={corners}
              data={organisationTypeOptions}
              value={value.organisationType}
              onChange={(organisationType) =>
                onChange({
                  ...value,
                  organisationType,
                  organisationUuid: null,
                  access: null,
                  makeResponsibleOwner: false,
                  existingDeveloperAction: null,
                })
              }
              hasError={hasFieldError(errors, "organisationType")}
            />
          </Column>
          {!hasProjectPicker && !createNewOrganisation && (
            <Column span={6}>
              <Autocomplete
                label="Organisation"
                placeholder={
                  isTypeChosen ? "Start typing to find the organisation" : "Choose an organisation type first"
                }
                corners={corners}
                data={organisationOptions}
                initialKey={value.organisationUuid}
                disabled={!isTypeChosen}
                // Typing reports "", which withdraws the organisation.
                onSelect={(organisationUuid) => onChange({ ...value, organisationUuid: organisationUuid || null })}
                onTypeSearch={onOrganisationSearch}
              />
            </Column>
          )}
        </Row>

        {createNewOrganisation && (
          <NotOnKanaDeveloperInviteFields
            value={skeletonOrgDetails}
            onChange={(details) => onSkeletonOrgDetailsChange?.(details)}
            errors={errors}
          />
        )}

        {/* The project takes the first slot, so the organisation drops down beside Access. */}
        <Row spacingV={hasProjectPicker && showMakeResponsibleOwner ? "ml" : "4xl"}>
          {hasProjectPicker && !createNewOrganisation && (
            <Column span={6}>
              <Autocomplete
                label="Organisation"
                placeholder={
                  isTypeChosen ? "Start typing to find the organisation" : "Choose an organisation type first"
                }
                corners={corners}
                data={organisationOptions}
                initialKey={value.organisationUuid}
                disabled={!isTypeChosen}
                // Typing reports "", which withdraws the organisation.
                onSelect={(organisationUuid) => onChange({ ...value, organisationUuid: organisationUuid || null })}
                onTypeSearch={onOrganisationSearch}
              />
            </Column>
          )}
          <Column span={6}>
            <Select
              label="Access"
              placeholder={isTypeChosen ? "Select" : "Choose an organisation type first"}
              corners={corners}
              data={accessOptions}
              value={value.access}
              disabled={!isTypeChosen}
              onChange={(access) =>
                onChange({ ...value, access, makeResponsibleOwner: false, existingDeveloperAction: null })
              }
              hasError={hasFieldError(errors, "access")}
            />
          </Column>
          {!hasProjectPicker && showMakeResponsibleOwner && <Column span={6}>{makeResponsibleOwnerField}</Column>}
        </Row>

        {hasProjectPicker && showMakeResponsibleOwner && (
          <Row spacingV="4xl">
            <Column span={6}>{makeResponsibleOwnerField}</Column>
          </Row>
        )}

        {showExistingDeveloperAction && (
          <Row spacingV="4xl">
            <Column span={6}>
              <Select
                label={`What happens to ${existingDeveloperName ?? "the current developer"}?`}
                placeholder="Select"
                corners={corners}
                data={existingDeveloperActionOptions}
                value={value.existingDeveloperAction ?? null}
                onChange={(existingDeveloperAction) => onChange({ ...value, existingDeveloperAction })}
                hasError={hasFieldError(errors, "existingDeveloperAction")}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column span={6}>
            <ActionButtons
              submitButtonLabel="Send invitation"
              corners={corners}
              onCancel={onCancel}
              isLoading={isLoading}
            />
          </Column>
        </Row>
      </form>
    </Card>
  );
};
